import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useGoogleLogin } from '@react-oauth/google'
import { useAuthStore } from '../stores/authStore'
import './Register.css'

const GOOGLE_ENABLED = !!(import.meta.env.VITE_GOOGLE_CLIENT_ID)

export default function Register() {
  const navigate = useNavigate()
  const { user, register, loginWithGoogle } = useAuthStore()

  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  useEffect(() => {
    if (user) navigate('/')
  }, [user, navigate])

  const googleSignup = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true)
      setError('')
      try {
        await loginWithGoogle(tokenResponse.access_token, 'access_token')
        navigate('/')
      } catch (err: any) {
        setError(err.message || 'Google sign-up failed')
      } finally {
        setGoogleLoading(false)
      }
    },
    onError: () => setError('Google sign-in was cancelled or failed'),
    flow: 'implicit',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!fullName.trim()) { setError('Please enter your full name'); return }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return }
    if (password !== confirm) { setError('Passwords do not match'); return }
    setLoading(true)
    try {
      await register(fullName.trim(), email.trim(), password)
      navigate('/')
    } catch (err: any) {
      setError(err.message || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="reg-container">
      <div className="reg-box">
        <h1>Nexus</h1>
        <h2>Create Account</h2>
        <p className="reg-subtitle">Join the drone management platform</p>

        {GOOGLE_ENABLED && (
          <>
            <button type="button" className="reg-google-btn"
              onClick={() => googleSignup()} disabled={googleLoading || loading}>
              <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              {googleLoading ? 'Connecting…' : 'Continue with Google'}
            </button>
            <div className="reg-divider"><span>or register with email</span></div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <label>Full Name
            <input type="text" placeholder="John Smith" value={fullName}
              onChange={e => setFullName(e.target.value)} required autoComplete="name" />
          </label>
          <label>Email
            <input type="email" placeholder="you@example.com" value={email}
              onChange={e => setEmail(e.target.value)} required autoComplete="email" dir="ltr" />
          </label>
          <label>Password <span className="reg-hint">(min. 6 characters)</span>
            <input type="password" placeholder="••••••••" value={password}
              onChange={e => setPassword(e.target.value)} required autoComplete="new-password" />
          </label>
          <label>Confirm Password
            <input type="password" placeholder="••••••••" value={confirm}
              onChange={e => setConfirm(e.target.value)} required autoComplete="new-password" />
          </label>
          {error && <div className="reg-error">{error}</div>}
          <button type="submit" className="reg-submit" disabled={loading || googleLoading}>
            {loading ? 'Creating account…' : 'Create Account'}
          </button>
        </form>

        <p className="reg-login-link">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  )
}
